import { AddressType, RatesType } from '../../../store/reducers/GetRates/Type';
import { BoxType, RatesInputType, WeightUnitType } from '../Type';
import { defaultBox, SHIPMENT_TYPES } from '../Constant';
import { calculateVolume } from '../../../services/calculateVolume';
import { inputRatesSchema } from '../RatesInput/Schema';

export const isExcelFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  return (
    name.endsWith('.xlsx') ||
    name.endsWith('.xls') ||
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    file.type === 'application/vnd.ms-excel'
  );
};

export interface MappedGetRatesData {
  pickup: AddressType;
  destination: AddressType;
  pickupPinCode: string;
  destinationPinCode: string;
  shipmentType: string;
  weight: number;
  weightUnit: WeightUnitType;
  boxes: BoxType[];
  hideBoxes: boolean;
  boxCount: number;
  chargeableWeight: number;
  isValid: boolean;
  missingFields: string[];
}

export const mapAiResponseToGetRates = (
  fields: any,
  countries: any[] = [],
  countryList: any[] = [],
): MappedGetRatesData => {
  const getCountryName = (cName?: string) => {
    if (!cName) return '';
    const trimmed = String(cName).trim();
    const matchCountry = (countries || []).find(
      c =>
        c.name.toLowerCase() === trimmed.toLowerCase() ||
        (c.alternateNames && c.alternateNames.some((alt: string) => alt.toLowerCase() === trimmed.toLowerCase())) ||
        (c.code && c.code.toLowerCase() === trimmed.toLowerCase()),
    );
    if (matchCountry) return matchCountry.name;

    const listMatch = (countryList || []).find(
      c => c.value.toLowerCase() === trimmed.toLowerCase() || c.display.toLowerCase() === trimmed.toLowerCase(),
    );
    if (listMatch) return listMatch.value;

    return trimmed;
  };

  const inferCountry = (cName?: string, _city?: string, pincode?: string) => {
    if (cName) {
      const resolved = getCountryName(cName);
      if (resolved) return resolved;
    }
    const cleanPin = String(pincode || '').trim();
    if (/^\d{6}$/.test(cleanPin)) {
      const match = (countries || []).find(c => c.name?.toLowerCase() === 'india');
      if (match) return match.name;
      return 'India';
    }
    const match = (countries || []).find(c => c.name?.toLowerCase() === 'india');
    if (match) return match.name;
    return '';
  };

  // 1. Shipper / Pickup
  const shipper = fields?.shipper || {};
  const pickupCity = shipper.city ? String(shipper.city).trim() : '';
  const pickupState = shipper.state ? String(shipper.state).trim() : '';
  const pickupPincode = shipper.pincode !== undefined && shipper.pincode !== null ? String(shipper.pincode).trim() : '';
  const pickupCountry = inferCountry(shipper.country ? String(shipper.country) : undefined, pickupCity, pickupPincode);

  const pickup: AddressType & Record<string, any> = {
    city: pickupCity,
    state: pickupState,
    country: pickupCountry,
    pincode: pickupPincode,
    ...(shipper.line1 ? { line1: String(shipper.line1).trim() } : {}),
    ...(shipper.line2 ? { line2: String(shipper.line2).trim() } : {}),
    ...(shipper.name ? { name: String(shipper.name).trim() } : {}),
    ...(shipper.companyName ? { companyName: String(shipper.companyName).trim() } : {}),
  };

  // 2. Consignee / Destination
  const consignee = fields?.consignee || {};
  const destCity = consignee.city ? String(consignee.city).trim() : '';
  const destState = consignee.state ? String(consignee.state).trim() : '';
  const destPincode =
    consignee.pincode !== undefined && consignee.pincode !== null ? String(consignee.pincode).trim() : '';
  const destCountry = inferCountry(consignee.country ? String(consignee.country) : undefined, destCity, destPincode);

  const destination: AddressType & Record<string, any> = {
    city: destCity,
    state: destState,
    country: destCountry,
    pincode: destPincode,
    ...(consignee.line1 ? { line1: String(consignee.line1).trim() } : {}),
    ...(consignee.line2 ? { line2: String(consignee.line2).trim() } : {}),
    ...(consignee.name ? { name: String(consignee.name).trim() } : {}),
    ...(consignee.companyName ? { companyName: String(consignee.companyName).trim() } : {}),
  };

  // 3. Package & Shipment Type
  const pkg = fields?.package || {};
  const isDoc = typeof pkg.isDocument === 'boolean' ? pkg.isDocument : false;
  const shipmentType = isDoc ? 'document' : 'parcel';

  // 4. Weight Unit
  const uom = String(pkg.unitOfMeasurement || '').toLowerCase();
  const weightUnit: WeightUnitType = uom.includes('lb') ? 'lb' : 'kg';

  // 5. Boxes & Weight
  let parsedBoxes: BoxType[] = [];
  let hideBoxes = true;
  let hasDimensions = false;

  if (pkg.box && Array.isArray(pkg.box) && pkg.box.length > 0) {
    parsedBoxes = pkg.box.map((b: any, index: number) => {
      const len =
        b.length !== undefined && b.length !== null && b.length !== '' ? parseFloat(String(b.length)) || 0 : 0;
      const wid = b.width !== undefined && b.width !== null && b.width !== '' ? parseFloat(String(b.width)) || 0 : 0;
      const hgt =
        b.height !== undefined && b.height !== null && b.height !== '' ? parseFloat(String(b.height)) || 0 : 0;
      const wt = b.weight !== undefined && b.weight !== null && b.weight !== '' ? parseFloat(String(b.weight)) || 0 : 0;

      if (len > 0 || wid > 0 || hgt > 0) {
        hasDimensions = true;
      }

      return {
        id: index.toString(),
        length: len,
        width: wid,
        height: hgt,
        weight: wt,
        boxCopyCount: b.boxCopyCount !== undefined ? Number(b.boxCopyCount) : 0,
        ...(b.products ? { products: b.products } : {}),
      };
    });
  }

  let totalWeight = 0;
  if (parsedBoxes.length > 0) {
    totalWeight = parsedBoxes.reduce((acc, b) => acc + (Number(b.weight) || 0), 0);
  }

  if (totalWeight <= 0) {
    const rawFallbackWeight = pkg.grossWeight || pkg.weight || fields?.grossWeight || fields?.weight;
    if (rawFallbackWeight !== undefined && rawFallbackWeight !== null && rawFallbackWeight !== '') {
      totalWeight = parseFloat(String(rawFallbackWeight)) || 0;
    }
  }

  if (parsedBoxes.length === 0) {
    parsedBoxes = [{ ...defaultBox, weight: totalWeight }];
  } else if (parsedBoxes.length === 1 && parsedBoxes[0].weight === 0 && totalWeight > 0) {
    parsedBoxes[0].weight = totalWeight;
  }

  if (hasDimensions && shipmentType === 'parcel') {
    hideBoxes = false;
  }

  // Calculate volumetric & chargeable weight
  const dimensionUnit = weightUnit === 'kg' ? 'cm' : 'in';
  const volumetricSum = parsedBoxes.reduce((acc, b) => {
    const vol = calculateVolume(b.length, b.width, b.height, dimensionUnit as any);
    const wt = typeof b.weight === 'string' ? parseFloat(b.weight) || 0 : b.weight;
    return acc + (wt > vol ? wt : vol);
  }, 0);

  const chargeableWeight = volumetricSum > totalWeight ? volumetricSum : totalWeight;

  // Validate with inputRatesSchema
  const ratesInput: RatesInputType = {
    shipmentType,
    pickup,
    destination,
    pickupPinCode: pickupPincode,
    destinationPinCode: destPincode,
    weight: totalWeight,
    weightUnit,
  };

  const validationResult = inputRatesSchema.validate(ratesInput, { abortEarly: false });
  const isValid = !validationResult.error;

  const missingFields: string[] = [];
  if (validationResult.error) {
    validationResult.error.details.forEach(d => {
      const path = d.path.join('.');
      if (path.includes('pickupPinCode')) missingFields.push('Pickup Pincode');
      else if (path.includes('destinationPinCode')) missingFields.push('Destination Pincode');
      else if (path.includes('pickup.country')) missingFields.push('Pickup Country');
      else if (path.includes('destination.country')) missingFields.push('Destination Country');
      else if (path.includes('weight')) missingFields.push('Valid Weight');
      else missingFields.push(path);
    });
  }

  return {
    pickup,
    destination,
    pickupPinCode: pickupPincode,
    destinationPinCode: destPincode,
    shipmentType,
    weight: totalWeight,
    weightUnit,
    boxes: parsedBoxes,
    hideBoxes,
    boxCount: parsedBoxes.length,
    chargeableWeight,
    isValid,
    missingFields: Array.from(new Set(missingFields)),
  };
};

export const buildRatesPayload = (
  ratesData: MappedGetRatesData,
  isTarrifPricing: boolean,
  userId?: string,
  organizationName?: string,
): RatesType & { organizationName?: boolean; userId?: string } => {
  const {
    destination,
    destinationPinCode,
    pickup,
    shipmentType,
    weight,
    weightUnit,
    pickupPinCode,
    boxes,
    chargeableWeight,
  } = ratesData;
  const finalWeight = chargeableWeight > Number(weight) ? chargeableWeight : Number(weight);

  const obj: RatesType & { organizationName?: boolean; userId?: string } = {
    destination: { ...destination, pincode: destinationPinCode?.toString() },
    source: { ...pickup, pincode: pickupPinCode.toString() },
    shipmentType,
    weight: Number(weight),
    package: {
      box: boxes.map((box, ind) => ({
        weight: Number(box.weight) < 1 && ind === 0 ? finalWeight.toString() : box.weight.toString(),
        length: typeof box.length === 'string' ? parseInt(box.length) : box.length,
        width: typeof box.width === 'string' ? parseInt(box.width) : box.width,
        height: typeof box.height === 'string' ? parseInt(box.height) : box.height,
      })),
      unitOfMeasurement: weightUnit === 'kg' ? 'kg-cm' : 'lb-in',
    },
    document: shipmentType === SHIPMENT_TYPES[1].value,
    dropOff: true,
    rateFor: 'get-rates',
    organizationName: organizationName === 'Cargo Solutions Network',
  };

  if (!isTarrifPricing && userId) {
    obj.userId = userId;
  }

  return obj;
};
